import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle/AlertTitle'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import add from 'date-fns/add'
import startOfDay from 'date-fns/startOfDay'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { BackContinue } from '@components/back_continue/back_continue'
import { useBookingContext } from '@providers/booking/booking_context'
import { buildPetGroupsTempered } from '@shared/booking_groups'
import { MAX_WIDGET_SIZE, STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { i18WithParams as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { formatListWithConjunction } from 'components-ui/src/shared/common'
import { isTemperTestEnabled } from 'components-ui/src/shared/features'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useRedirectNoData } from 'hooks/use_redirect_no_data'
import { useClientBookingSearchByDays } from 'service-api/src/hooks/use_client_booking_search'
import { ProviderClientDto } from 'service-api/src/types/api'
import { DatePicker } from './date_picker'
import { DateViewer } from './date_viewer'
import { formatStayTime } from './helper'

interface Props {
  provider: ProviderClientDto | null
}

export const TemperTestInner = ({ provider }: Props) => {
  const bookingCtx = useBookingContext()
  const today = useMemo(() => new Date(), [])
  const tempTestRange = add(today, {
    minutes:
      bookingCtx.state.config.booking?.maxBookingWindow?.[0]?.min ||
      bookingCtx.state.config.booking?.defaultMaxBookingWindow ||
      0,
  })

  if (isTemperTestEnabled() && !provider) {
    return <Alert severity="error">{t('temperTest.providerNotConfigured')}</Alert>
  }

  return (
    <Stack direction={{ sx: 'column', md: 'row' }} alignItems="start" spacing={3} sx={{ width: '100%' }}>
      <DatePicker startDate={startOfDay(today)} maxDate={tempTestRange} provider={provider} />
      <DateViewer provider={provider} />
    </Stack>
  )
}

export const TemperTest = () => {
  const bookingCtx = useBookingContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const { navigateRoute } = useRoutes()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const [provider, setProvider] = useState<ProviderClientDto | null>(null)
  const [stayTime, setStayTime] = useState<number | null>(null)

  // redirect to home if the state has invalid data
  useRedirectNoData()

  useEffect(() => {
    bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'temperTest' } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.currentState])

  useEffect(() => {
    document.title = t('title.scheduleEvaluation')
  }, [])

  const temperTestData = useClientBookingSearchByDays(locationName, 'temper-test', {
    petGroups: buildPetGroupsTempered(bookingCtx.state),
    offersType: 'PRIMARY',
  })

  useEffect(() => {
    if (temperTestData.isSuccess) {
      const result = temperTestData.data?.results?.[0]
      const resource = result?.availabilityGroupsByDays?.[0]?.[0]?.availabilities?.[0].providers?.[0]
      const minutesPerCycle = result?.offer?.serviceOffers?.[0]?.service?.serviceResources?.[0]?.minutesPerCycle ?? null
      setProvider(resource?.id ? resource : null)
      setStayTime(minutesPerCycle)
    }
  }, [temperTestData.data?.results, temperTestData.isSuccess])

  const temperTestName = bookingCtx.state.data.temperTest?.displayName || t('temperTest.defaultName')
  const petNames = useMemo(
    () => bookingCtx.state.cart.groups.flatMap((group) => group.pets.map((pet) => pet.name)),
    [bookingCtx.state.cart.groups]
  )

  const canContinue = useMemo(() => {
    return (
      typeof bookingCtx.state.cart.temperTest?.date !== 'undefined' &&
      typeof bookingCtx.state.cart.temperTest?.time !== 'undefined'
    )
  }, [bookingCtx.state.cart.temperTest?.date, bookingCtx.state.cart.temperTest?.time])

  const onBackHandler = useCallback(
    (ev?: React.SyntheticEvent) => {
      ev?.preventDefault()
      navigateRoute('searchServiceType', { serviceTypeName: 'boarding' })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigateRoute, bookingCtx.state.serviceTypeName]
  )

  const pageTitle = t('temperTest.title', { temperTestName })
  const pageSubtitle = t('temperTest.subtitle', { temperTestName, petNames: formatListWithConjunction(petNames) })

  if (!bookingCtx.state.startDate) {
    return null
  }

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Typography variant="h1" sx={{ px: 2 }}>
        {pageTitle}
      </Typography>

      <Spacer height={2} />
      {temperTestData.isSuccess && (
        <Alert severity="info">
          <AlertTitle>{t('temperTest.stay', { stayTime: formatStayTime(stayTime) })}</AlertTitle>
          <Typography variant="body2">{t('temperTest.stay.desc')}</Typography>
        </Alert>
      )}

      <Spacer height={2} />

      <Paper elevation={1} sx={{ p: 2, borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px` }}>
        <Typography variant="h5">{pageSubtitle}</Typography>

        <Spacer height={2} />

        {temperTestData.isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={200} />
        ) : (
          <TemperTestInner provider={provider} />
        )}
      </Paper>

      <Spacer height={STICKY_FOOTER_HEIGHT} />
      <BackContinue continueEnabled={canContinue} onBack={onBackHandler} />
    </Box>
  )
}
